/**
 * Notification bell — persistent, top-right, independent of both nav web
 * components (see the CSS comment in index.html for why). Polls the unread
 * count rather than wiring a live PubSub/SSE topic — the design doc
 * (design/docs/notification_system.md) flags that as a real enhancement, not
 * required for a correct v1; polling is simple, always-consistent, and needs
 * no new AppBroadcast topic or server-side event on top of what already exists.
 */
import AuthController from '../services/AuthController.js';
import { capList } from './listExpand.js';
import { guardedFetch } from './navGeneration.js';

const apiBase = () => AuthController.getConfig?.().BASE_URL || '';
const ESC = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' };
const esc = (s) => String(s ?? '').replace(/[&<>"']/g, (c) => ESC[c]);
const POLL_MS = 5 * 60 * 1000; // unread count only — cheap, no reason to poll faster

function relTime(iso) {
  try {
    const s = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
    if (s < 60) return 'just now';
    if (s < 3600) return `${Math.floor(s / 60)}m ago`;
    if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
    return `${Math.floor(s / 86400)}d ago`;
  } catch { return ''; }
}

// The row's clear control is a real <button>. On a linked item that means a button
// inside an <a>, which is invalid HTML — so the row stays a <div> whenever it is
// clearable and the link becomes an explicit inner anchor instead. Keeps the click
// targets unambiguous too: clearing a notification should never also navigate.
//
// It reads "Clear", not "×". The glyph shipped first and the objection to it was the
// right one: a bare close-glyph is chrome, and chrome on a notification row is
// ambiguous — close the panel? mark it read? hide it forever? The word says which, and
// it is the SAME word as "Clear all" directly above it in the header, so the row action
// and the bulk action read as one verb at two scopes rather than two vocabularies.
function itemHTML(n) {
  // The row used to BE the <a>. It can't stay one: the clear control is a real
  // <button>, and a button inside an anchor is invalid HTML (and gives one click two
  // meanings). So the row is a <div> and the anchor moves inside, wrapping the whole
  // text block rather than just the title — the click target stays the full row minus
  // the clear button, which is what it was before.
  const inner = `
      <div class="m5t-notif-title">${esc(n.title)}</div>
      ${n.body ? `<div class="m5t-notif-body">${esc(n.body)}</div>` : ''}
      <div class="m5t-notif-time">${esc(relTime(n.created_tz))}</div>`;
  return `<div class="m5t-notif-item" data-guid="${esc(n.guid)}" data-read="${n.read}" role="menuitem">
    ${n.link
      ? `<a class="m5t-notif-main m5t-notif-link" href="${esc(n.link)}">${inner}</a>`
      : `<div class="m5t-notif-main">${inner}</div>`}
    <button class="m5t-notif-dismiss" type="button" aria-label="Clear “${esc(n.title)}”" data-dismiss="${esc(n.guid)}">Clear</button>
  </div>`;
}

const SKELETON = `<div class="m5t-notif-skel"><span></span><span></span><span></span></div>`.repeat(3);

// `hidden` is display:none and cannot transition, so open state lives on data-open.
// visibility (in the CSS) still takes a closed panel out of the a11y tree and tab order.
const isPanelOpen = (panel) => panel.dataset.open === 'true';
const setPanelOpen = (panel, open) => { panel.dataset.open = open ? 'true' : 'false'; };

export function setNotificationBellVisible(show) {
  const el = document.getElementById('m5t-notif-bell');
  if (el) el.hidden = !show;
  if (!show) {
    const panel = document.getElementById('m5t-notif-panel');
    if (panel) setPanelOpen(panel, false);
  }
}

// The bell is `position: fixed` — deliberately out of document flow, so it stays
// visible regardless of scroll (see index.html's own comment on #m5t-notif-bell).
// Its hardcoded `top` default only ever looked right by coincidence: the
// org-context-banner (main.js's renderOrgContextBanner()) is a normal-flow block
// ABOVE it with its own padding/height, and can wrap onto a second line
// (.ocb's flex-wrap: wrap) — so a single fixed pixel offset can't stay centered
// against the banner's actual buttons in every case. Re-measures the banner's own
// .ocb-btn directly (not just the banner's overall box height), so this stays
// correct even wrapped, where the buttons end up on a different line than the
// banner's leading icon/text. A ResizeObserver on the banner (not a resize/scroll
// listener) catches every case that changes its rendered height: content
// changes (preview toggle, org switch), window resize, font load.
const BELL_DEFAULT_TOP = 14;
let bellAlignObserver = null;
// Separate observer for "the bell became visible", which the ResizeObserver above
// cannot detect — a hidden element never resizes, it simply has no box.
let bellVisibilityObserver = null;

/**
 * Wire the bell's vertical position to the org-context-banner's actual button
 * position whenever the banner is showing, falling back to the original fixed
 * default otherwise. Call once, right when the banner element is first created
 * (idempotent — a second call just replaces the observer harmlessly).
 */
export function alignNotifBellToBanner(banner) {
  if (typeof ResizeObserver === 'undefined' || !banner) return; // no dynamic realignment on an ancient browser — the fixed default still applies
  const bell = document.getElementById('m5t-notif-bell');
  const bellBtn = document.getElementById('m5t-notif-bell-btn');
  if (!bell || !bellBtn) return;

  const apply = () => {
    const ctaBtn = !banner.hidden ? banner.querySelector('.ocb-btn') : null;
    if (!ctaBtn) { bell.style.top = `${BELL_DEFAULT_TOP}px`; return; }

    // Centring needs the bell's own height, and a hidden element measures 0x0 —
    // `display: none` produces no box at all. Computing from that silently yields an
    // offset half a bell too high, and nothing recomputes it afterwards unless the
    // banner happens to resize again.
    //
    // apply() genuinely does run while the bell is still hidden: renderOrgContextBanner
    // calls this the moment it CREATES the banner element, which is before the banner
    // has content and (on a cold load) before the route handler has shown the bell.
    // Verified in a browser — the mutation log shows an inline top written at ~525ms
    // with hidden=true and the button measuring 0.
    //
    // So defer rather than guess: leave the last good value, and let the observer
    // below recompute the instant the bell becomes measurable.
    const bellSize = bellBtn.getBoundingClientRect().height;
    if (!bellSize) return;

    const r = ctaBtn.getBoundingClientRect();
    bell.style.top = `${Math.round(r.top + (r.height - bellSize) / 2)}px`;
  };

  bellAlignObserver?.disconnect();
  bellAlignObserver = new ResizeObserver(apply);
  bellAlignObserver.observe(banner);

  // The banner resizing is not the only thing that invalidates the position — the bell
  // becoming visible does too, and that is the case the ResizeObserver cannot see.
  bellVisibilityObserver?.disconnect();
  bellVisibilityObserver = new MutationObserver(apply);
  bellVisibilityObserver.observe(bell, { attributes: true, attributeFilter: ['hidden'] });

  apply(); // avoid a one-frame flash at the old position before the observer's first async callback
}

let pollTimer = null;
// Document-level handlers are kept here so a second mount can detach the first set
// before attaching its own — the same idempotency `clearInterval(pollTimer)` already
// gives the poll. Without it every mount stacks another listener on `document` and
// they all keep firing; the badge-sync tests, which mount per case, surfaced this by
// counting one refresh per historical mount instead of one.
let docClickHandler = null;
let docVisibilityHandler = null;

export function mountNotificationBell() {
  const btn = document.getElementById('m5t-notif-bell-btn');
  const badge = document.getElementById('m5t-notif-badge');
  const panel = document.getElementById('m5t-notif-panel');
  if (!btn || !badge || !panel) return;

  const setCount = (n) => {
    badge.textContent = n > 99 ? '99+' : String(n);
    badge.hidden = !n;
  };

  const refreshCount = async () => {
    try {
      const res = await guardedFetch(`${apiBase()}/m5t/v5/notifications/unread-count`);
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.ok) setCount(data.count || 0);
    } catch { /* polling — a miss just tries again next cycle */ }
  };

  // Reconcile the badge against the list that was just rendered.
  //
  // The badge and the list were two independent fetches that never spoke to each
  // other: refreshCount ran on mount, on the 5-minute poll, and after each mutating
  // action — but NOTHING re-ran it when the list loaded. So opening the panel,
  // finding it empty and closing it left the badge still claiming "4" until the next
  // poll, up to five minutes later.
  //
  // Not a rare race either, because read state is org-wide by design (coreNotification's
  // DDL: "Read state is org-wide (read_time), not per-user"). A teammate clearing
  // notifications in their session empties this list for everyone, while every other
  // open tab keeps its own stale number.
  //
  // Derived from the list rather than re-fetched, so the badge cannot contradict the
  // rows actually on screen — that contradiction IS the reported bug. One exception:
  // the server pages this list, so a full page may be truncated and counting it would
  // undercount. There, defer to the authoritative endpoint instead.
  const LIST_PAGE_SIZE = 50; // must match the default limit in service-api's notifications.js
  const reconcileCountWithList = (notes) => {
    if (notes.length >= LIST_PAGE_SIZE) { refreshCount(); return; }
    setCount(notes.filter((n) => !n.read).length);
  };

  const headHTML = (actions = '') =>
    `<div class="m5t-notif-head">Notifications${actions ? `<span class="m5t-notif-head__actions">${actions}</span>` : ''}</div>`;

  const renderEmpty = () => {
    // Clearing the last item leaves the panel open — replace the list in place rather
    // than closing, so the result of the action is visible instead of inferred.
    panel.querySelectorAll('.m5t-notif-item, .m5t-notif-empty').forEach((el) => el.remove());
    panel.querySelector('.m5t-notif-head__actions')?.remove();
    panel.insertAdjacentHTML('beforeend', '<div class="m5t-notif-empty">You’re all caught up.</div>');
  };

  const loadList = async () => {
    panel.innerHTML = headHTML() + SKELETON;
    try {
      const res = await guardedFetch(`${apiBase()}/m5t/v5/notifications`);
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.ok) throw new Error();
      const notes = data.notifications || [];
      const actions = [
        notes.some((n) => !n.read) ? '<button class="m5t-notif-markall" type="button">Mark all read</button>' : '',
        notes.length ? '<button class="m5t-notif-clearall" type="button">Clear all</button>' : '',
      ].join('');
      panel.innerHTML = headHTML(actions) +
        (notes.length ? notes.map(itemHTML).join('') : '<div class="m5t-notif-empty">You’re all caught up.</div>');
      // A 50-row dropdown is a scroll trap; same cap as every other list.
      capList(panel, { rowSelector: '.m5t-notif-item', noun: 'notifications' });
      reconcileCountWithList(notes);
    } catch {
      panel.innerHTML = headHTML() + '<div class="m5t-notif-empty">Could not load. Try again.</div>';
      // Deliberately NOT reconciling here: a failed load says nothing about the real
      // unread count, and zeroing the badge on a network blip would hide notifications
      // that do exist. The last known count is the better answer.
    }
  };

  const closePanel = () => { setPanelOpen(panel, false); btn.setAttribute('aria-expanded', 'false'); };
  const openPanel = async () => {
    setPanelOpen(panel, true);
    btn.setAttribute('aria-expanded', 'true');
    await loadList();
  };

  btn.addEventListener('click', (e) => {
    e.stopPropagation();
    if (isPanelOpen(panel)) closePanel(); else openPanel();
  });
  // Tap/click anywhere outside closes it — the bell's own dropdown is the one
  // exception in this app to "no document-level dismiss listener" (see the
  // dashboard chart tooltip's opposite call): there's exactly one bell, mounted
  // once for the life of the app shell, so this listener never re-accumulates
  // across re-renders the way a per-chart one attached on every data refresh would.
  // Re-check the count when the tab comes back to the foreground. Small addition
  // beyond the panel-open reconciliation above, included because it targets the same
  // bug from the other side: with org-wide read state, a badge can go stale while
  // this tab sits in the background, and a 5-minute poll is otherwise the only thing
  // that corrects it. Safe to attach at document level for the same reason the
  // dismiss listener below is — one bell, mounted once for the life of the app shell.
  if (docVisibilityHandler) document.removeEventListener('visibilitychange', docVisibilityHandler);
  docVisibilityHandler = () => { if (document.visibilityState === 'visible') refreshCount(); };
  document.addEventListener('visibilitychange', docVisibilityHandler);

  if (docClickHandler) document.removeEventListener('click', docClickHandler);
  docClickHandler = (e) => {
    if (isPanelOpen(panel) && !panel.contains(e.target) && e.target !== btn) closePanel();
  };
  document.addEventListener('click', docClickHandler);

  // Escape closes it, matching every other dismissible surface in the app.
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && isPanelOpen(panel)) { closePanel(); btn.focus(); }
  });

  panel.addEventListener('click', async (e) => {
    // Dismiss one. Checked before the mark-read branch below: the button lives inside
    // the row, so without this a dismissal would also register as "read this item".
    const dismissBtn = e.target.closest('[data-dismiss]');
    if (dismissBtn) {
      e.preventDefault();
      e.stopPropagation();
      const row = dismissBtn.closest('.m5t-notif-item');
      const guid = dismissBtn.dataset.dismiss;
      const wasUnread = row?.dataset.read === 'false';
      row?.setAttribute('data-leaving', 'true');
      // Optimistic: the row leaves now and the request settles behind it. A failure
      // leaves the server row intact and the next open shows it again, which is the
      // honest outcome — better than blocking the interaction on a round trip.
      try {
        await guardedFetch(`${apiBase()}/m5t/v5/notifications/${encodeURIComponent(guid)}`, {
          method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ status: 'DISMISSED' }),
        });
      } catch { /* best-effort */ }
      // Match the CSS leave transition, then drop the node.
      setTimeout(() => {
        row?.remove();
        if (!panel.querySelector('.m5t-notif-item')) renderEmpty();
      }, 180);
      if (wasUnread) refreshCount();
      return;
    }

    // Clear all.
    const clearAll = e.target.closest('.m5t-notif-clearall');
    if (clearAll) {
      e.preventDefault();
      clearAll.disabled = true;
      panel.querySelectorAll('.m5t-notif-item').forEach((el) => el.setAttribute('data-leaving', 'true'));
      try { await guardedFetch(`${apiBase()}/m5t/v5/notifications/dismiss-all`, { method: 'PATCH' }); } catch { /* best-effort */ }
      setTimeout(renderEmpty, 180);
      refreshCount();
      return;
    }

    const item = e.target.closest('.m5t-notif-item');
    if (item && item.dataset.read === 'false') {
      const guid = item.dataset.guid;
      item.dataset.read = 'true';
      try {
        await guardedFetch(`${apiBase()}/m5t/v5/notifications/${encodeURIComponent(guid)}`, {
          method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ read: true }),
        });
        refreshCount();
      } catch { /* best-effort — the item still visually marks read locally */ }
      return; // let an <a> item's own navigation proceed after marking read
    }
    const markAll = e.target.closest('.m5t-notif-markall');
    if (markAll) {
      e.preventDefault();
      panel.querySelectorAll('.m5t-notif-item').forEach((el) => { el.dataset.read = 'true'; });
      markAll.remove();
      try { await guardedFetch(`${apiBase()}/m5t/v5/notifications/read-all`, { method: 'PATCH' }); } catch { /* best-effort */ }
      refreshCount();
    }
  });

  refreshCount();
  clearInterval(pollTimer);
  pollTimer = setInterval(refreshCount, POLL_MS);
}
